import { createPrototypeApiClient } from './apiClient';
import { prototypeRuntimeConfig } from './prototypeRuntimeConfig';
import { createTurnstileTokenProvider } from './turnstileClient';
import type { PrototypeState } from './prototypeHydration';
import { readInlineSessionModel } from './inlineSessionModel';
import type { PlanCode } from './prototypeTypes';

function deriveLocalIpHash(): string {
	const seed = `${navigator.userAgent || 'ua'}|${navigator.language || 'en'}|${Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}`;
	let hash = 0x811c9dc5;
	for (let index = 0; index < seed.length; index++) {
		hash ^= seed.charCodeAt(index);
		hash = (hash * 0x01000193) >>> 0;
	}
	return hash.toString(16).padStart(8, '0');
}

export function createPrototypeState(): PrototypeState {
	// Anti-flicker: the inline model seeds login state, plan, and avatar initials.
	// Full identity fields remain at safe defaults until session hydration.
	const inline = readInlineSessionModel();
	const isLoggedIn = inline?.viewer?.authStatus === "authenticated";
	const initialAccessPlan: PlanCode = (inline?.viewer?.plan as PlanCode) ?? 'free';
	const api = createPrototypeApiClient();
	api.setAuthenticated(isLoggedIn);

	return {
		runtime: {
			api,
			billingCycle: 'quarterly',
			activeCourseId: '',
			activeChapterIndex: 2,
			chapterIdByIndex: {},
			resumeProgressByChapter: {},
			mediaResumeSecondsByChapter: {},
			audioSecondsByChapter: {},
			videoSecondsByChapter: {},
			lastApiError: null,
			userId: prototypeRuntimeConfig.defaultUserId,
			userEmail: prototypeRuntimeConfig.defaultUserEmail,
			userDisplayName: inline?.viewer.avatarInitials ?? '',
			subscriptionId: prototypeRuntimeConfig.defaultSubscriptionId,
			sessionHash: '',
			ipHash: deriveLocalIpHash(),
			turnstile: createTurnstileTokenProvider(null),
			// Remains fail-closed until /api/public-config resolves. Hydration
			// installs either the real browser provider or the explicit local-dev
			// provider, then re-renders any auth modal opened during bootstrap.
			interactiveTurnstile: undefined,
		},
		isLoggedIn,
		// The minimal inline payload cannot establish billing identity. Purchase
		// remains fail-closed until /api/auth/session supplies PlanContext.
		currentPlan: isLoggedIn ? 'free' : 'visitor',
		effectiveAccessPlan: initialAccessPlan,
		planContext: null,
	};
}

export function ensureToastContainer(): void {
	if (typeof document === 'undefined' || document.getElementById('toastContainer')) return;
	const container = document.createElement('div');
	container.id = 'toastContainer';
	container.className = 'toast-container';
	container.setAttribute('aria-live', 'polite');
	container.setAttribute('aria-atomic', 'false');
	document.body.appendChild(container);
}
